/* ============================================================================
   SignIn tadmin — pantalla de acceso Admin (Yool Design System)
   ---------------------------------------------------------------------------
   Compone YoolAuthSplit + YoolTag del bundle (window.DesignSystemRemix_1a4bd3),
   variante Admin calcada 1:1 del demo components/YoolAuthSplit/YoolAuthSplit.html.
   Solo tokens centralizados (var(--yool-*), --radius-*, --font-sans); el teal
   queda en el CTA del propio componente. Auth real vía Clerk (password +
   Microsoft OAuth).
   ============================================================================ */
import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { toast } from 'sonner';

const DS = () => (typeof window !== 'undefined' && window.DesignSystemRemix_1a4bd3) || {};
const asset = (p) => (process.env.PUBLIC_URL || '') + p;

/* Config Admin — espejo de APPS.admin en YoolAuthSplit.html (card del DS). */
const ADMIN_CFG = {
  mediaSide: 'left',
  photo: asset('/ds/assets/photos/yool-b2b-editorial.jpg'),
  panelColor: 'var(--yool-navy, #0E2640)',
  accent: 'var(--yool-cyan, #06B6D4)',
  appName: 'Admin',
  panelEyebrow: 'Back office interno',
  panelTitle: 'Opera Yool',
  panelTitleAccent: 'de punta a punta.',
  panelText: 'Pólizas, compañías, cobros y equipo en un solo panel.',
  guarantees: ['Acceso por rol', 'Auditoría completa', 'Datos en Chile'],
  formEyebrow: 'AdminYool',
  formTitle: 'Inicia sesión',
  formSubtitle: 'Usa tu cuenta corporativa Yool.',
  sso: ['microsoft'],
  footer: null,
  submitLabel: 'Entrar al panel',
};

function clerkErrorMessage(err) {
  const e = err && err.errors && err.errors[0];
  return (e && (e.longMessage || e.message)) || 'No se pudo iniciar sesión. Inténtalo de nuevo.';
}

export default function SignIn() {
  const navigate = useNavigate();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();
  const busyRef = React.useRef(false);

  if (authLoaded && isSignedIn) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async ({ email, password }) => {
    if (!isLoaded || busyRef.current) return;
    if (!email || !password) {
      toast.error('Ingresa tu correo y contraseña.');
      return;
    }
    busyRef.current = true;
    try {
      const res = await signIn.create({ identifier: email, password });
      if (res.status === 'complete') {
        await setActive({ session: res.createdSessionId });
        navigate('/dashboard', { replace: true });
      } else {
        toast.error('Tu cuenta requiere un paso de verificación adicional. Usa "Continuar con Microsoft".');
      }
    } catch (err) {
      toast.error(clerkErrorMessage(err));
    } finally {
      busyRef.current = false;
    }
  };

  const handleSSO = (provider) => {
    if (!isLoaded) return;
    signIn
      .authenticateWithRedirect({
        strategy: `oauth_${provider}`,
        redirectUrl: '/sign-in/sso-callback',
        redirectUrlComplete: '/dashboard',
      })
      .catch((err) => toast.error(clerkErrorMessage(err)));
  };

  const { YoolAuthSplit, YoolTag } = DS();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--yool-page, #F7F7F7)',
        fontFamily: 'var(--font-sans)',
        display: 'grid',
        placeItems: 'center',
        padding: '40px 32px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 1120, height: 'min(680px, calc(100vh - 80px))', minHeight: 560 }}>
        {YoolAuthSplit ? (
          <YoolAuthSplit
            {...ADMIN_CFG}
            logo={asset('/ds/assets/yool-logo-white.svg')}
            back={{ label: 'Volver al inicio', onClick: () => navigate('/') }}
            brandTag={YoolTag ? (
              /* Trío FancyYool "Digital" variante dark (Yool Tag v2) — sobre navy/foto. */
              <YoolTag
                ambiente="admin"
                size={13}
                colors={[
                  'var(--fy-dark-teal, #2DD4BF)',
                  'var(--fy-dark-cyan, #22D3EE)',
                  'var(--fy-sky, #0EA5E9)',
                ]}
              />
            ) : null}
            legal={
              <span>
                Al continuar aceptas los{' '}
                <a href="#" onClick={(e) => e.preventDefault()} style={{ color: 'var(--yool-cyan, #06B6D4)', textDecoration: 'underline' }}>Términos</a>
                {' '}y la{' '}
                <a href="#" onClick={(e) => e.preventDefault()} style={{ color: 'var(--yool-cyan, #06B6D4)', textDecoration: 'underline' }}>Política de privacidad</a>.
              </span>
            }
            onSubmit={handleSubmit}
            onSSO={handleSSO}
            onForgot={() => toast.info('Pide a un administrador restablecer tu contraseña, o entra con Microsoft.')}
          />
        ) : (
          /* Fallback si el bundle del DS no cargó: mismo flujo, mínima superficie tokenizada. */
          <div
            style={{
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              background: '#fff',
              border: '1px solid var(--yool-border, #E0E0E0)',
              borderRadius: 16,
            }}
          >
            <div style={{ textAlign: 'center', padding: 32 }}>
              <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-.03em', color: 'var(--yool-black, #1A1A1A)' }}>
                Inicia sesión
              </h1>
              <p style={{ margin: '10px 0 24px', fontSize: 14.5, color: 'var(--yool-gray1, #54585A)' }}>
                Usa tu cuenta corporativa Yool.
              </p>
              <button
                type="button"
                onClick={() => handleSSO('microsoft')}
                style={{
                  height: 44,
                  padding: '0 24px',
                  borderRadius: 'var(--radius-sm, 8px)',
                  border: 'none',
                  cursor: 'pointer',
                  background: 'var(--yool-cta, #007D6E)',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Continuar con Microsoft
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
